import { C } from './theme';
import { findExercise } from './exerciseDB';
import { REP_RANGE_DEFAULT } from './constants';

function _olsSlope(xs, ys) {
  const n = xs.length;
  const xMean = xs.reduce((a, b) => a + b, 0) / n;
  const yMean = ys.reduce((a, b) => a + b, 0) / n;
  const ssXX = xs.reduce((s, x) => s + (x - xMean) ** 2, 0);
  const ssXY = xs.reduce((s, x, i) => s + (x - xMean) * (ys[i] - yMean), 0);
  return ssXX === 0 ? 0 : ssXY / ssXX;
}

// ─── EVIDENCE BASE ────────────────────────────────────────────────────────────
export const EVIDENCE_VERSION = '2026-05';

export const CITATIONS = {
  schoenfeld2017volume:   { tier:2, score:85, text:'Schoenfeld BJ et al. (2017) J Sports Sci 35(11):1073-82',       doi:'10.1080/02640414.2016.1210197' },
  krieger2010sets:        { tier:2, score:80, text:'Krieger JW (2010) J Strength Cond Res 24(4):1150-9',            pubmed:'20300012' },
  bernardez2022umbrella:  { tier:1, score:90, text:'Bernárdez-Vázquez R et al. (2022) Front Sports Act Living 4:949021', doi:'10.3389/fspor.2022.949021' },
  bazValle2022volume:     { tier:2, score:80, text:'Baz-Valle E et al. (2022) J Hum Kinet 81:199-210',              doi:'10.2478/hukin-2022-0017' },
  zourdos2016rir:         { tier:3, score:60, text:'Zourdos MC et al. (2016) J Strength Cond Res 30(1):267-75',     pubmed:'25993662' },
  plotkin2022progression: { tier:3, score:60, text:'Plotkin D et al. (2022) PeerJ 10:e14142',                       doi:'10.7717/peerj.14142' },
  lesuer1997formulas:     { tier:3, score:55, text:'LeSuer DA et al. (1997) J Strength Cond Res 11(4):211-3',       pubmed:'9220004' },
  meeusen2013overtraining:{ tier:5, score:30, text:'Meeusen R et al. (2013) Med Sci Sports Exerc 45(1):186-205',    pubmed:'23247672' },
  refalo2023failure:      { tier:2, score:80, text:'Refalo MC et al. (2023) Sports Med 53(3):649-665',              pubmed:'36334240' },
  schoenfeld2016rest:     { tier:3, score:65, text:'Schoenfeld BJ et al. (2016) J Strength Cond Res 30(7):1805-12', pubmed:'26605807' },
  schoenfeld2019frequency:{ tier:2, score:80, text:'Schoenfeld BJ et al. (2019) J Sports Sci 37(11):1286-95',      pubmed:'30558493' },
  schoenfeld2017load:     { tier:2, score:80, text:'Schoenfeld BJ et al. (2017) J Strength Cond Res 31(12):3508-23', pubmed:'28834797' },
  israetel2021book:       { tier:5, score:25, text:'Israetel M et al. (2021) Scientific Principles of Hypertrophy Training. RP 4th ed.', doi:null },
};

export const EVIDENCE_LABEL = score =>
  score >= 80 ? 'Forte' : score >= 60 ? 'Moderato' : score >= 40 ? 'Basso' : score >= 20 ? 'Euristica' : 'Non supportato';

const _evidenceDetail = (score, sourceKeys) => {
  const label = EVIDENCE_LABEL(score);
  const srcs = (sourceKeys||[]).map(k => CITATIONS[k]?.text).filter(Boolean).join('; ');
  return `Evidenza: ${score}/100 (${label})${srcs ? `: ${srcs}` : ''}`;
};

// ─── VOLUME LANDMARKS ─────────────────────────────────────────────────────────
// MV/MEV/MAV: Schoenfeld 2017 + Bernárdez-Vázquez 2022 + Baz-Valle 2022 (score 85)
// MRV e valori per muscolo specifici: Israetel 2021 (T5, expertEstimate:true, score 25)
export const VOLUME_LANDMARKS = {
  default:          { mv:4, mev:6, mav_lo:10, mav_hi:20, mrv:25, expertEstimate:false },
  pettorali:        { mv:4, mev:6, mav_lo:10, mav_hi:20, mrv:22, expertEstimate:true },
  dorsali:          { mv:4, mev:6, mav_lo:10, mav_hi:18, mrv:25, expertEstimate:true },
  quadricipiti:     { mv:4, mev:6, mav_lo:10, mav_hi:18, mrv:20, expertEstimate:true },
  bicipiti:         { mv:4, mev:6, mav_lo:10, mav_hi:20, mrv:26, expertEstimate:true },
  tricipiti:        { mv:4, mev:6, mav_lo:10, mav_hi:18, mrv:22, expertEstimate:true },
  deltoidi:         { mv:4, mev:6, mav_lo:16, mav_hi:22, mrv:26, expertEstimate:true },
  glutei:           { mv:4, mev:6, mav_lo:10, mav_hi:20, mrv:25, expertEstimate:true },
  'ischio-crurali': { mv:4, mev:6, mav_lo:10, mav_hi:16, mrv:20, expertEstimate:true },
  polpacci:         { mv:4, mev:6, mav_lo:12, mav_hi:16, mrv:20, expertEstimate:true },
  addominali:       { mv:4, mev:6, mav_lo:10, mav_hi:16, mrv:20, expertEstimate:true },
};

// ─── SCIENCE ─────────────────────────────────────────────────────────────────
export const rpe2rir = rpe => Math.max(0, 10 - parseFloat(rpe || 0));
export const rir2rpe = rir => Math.min(10, 10 - parseFloat(rir || 0));

export function epley1RM(w, r, rir = 0) {
  const rEff = Math.max(1, parseInt(r||0) + Math.round(parseFloat(rir||0)));
  const wN = parseFloat(w||0);
  if (!wN || rEff <= 0) return 0;
  return rEff === 1 ? wN : wN * (1 + rEff / 30);
}
function brzycki1RM(w, r, rir = 0) {
  const rEff = Math.max(1, parseInt(r||0) + Math.round(parseFloat(rir||0)));
  const wN = parseFloat(w||0);
  if (!wN || rEff <= 0 || rEff >= 37) return 0;
  const denom = 1.0278 - 0.0278 * rEff;
  return denom > 0 ? wN / denom : 0;
}
export function filterWarmupSets(sets) {
  if (!Array.isArray(sets) || sets.length < 2) return sets;
  const weights = sets.map(s => parseFloat(s.weight) || 0);
  const maxW = Math.max(...weights);
  if (maxW <= 0) return sets;
  const peakIdx = weights.indexOf(maxW);
  if (peakIdx === 0) return sets;
  // Only discard as warmup if weight is below 65% of max — avoids dropping
  // legitimate working sets that are slightly lighter than the top set
  const threshold = maxW * 0.65;
  return sets.filter((_, i) => i >= peakIdx || weights[i] >= threshold);
}

export function calcVolume(sets) {
  if(!Array.isArray(sets))return 0;
  return filterWarmupSets(sets).reduce((s, x) => {
    const w = parseFloat(x.weight||0), r = parseInt(x.reps||0);
    return s + (w>0&&r>0 ? w*r : 0);
  }, 0);
}
export function calcAvg1RM(sets) {
  const v = filterWarmupSets(sets).filter(s => parseFloat(s.weight)>0 && parseInt(s.reps)>0);
  if (!v.length) return 0;
  return v.reduce((s,x) => {
    const rEff = parseInt(x.reps||0) + Math.round(parseFloat(x.rir||0));
    const ep = epley1RM(x.weight, x.reps, x.rir);
    const br = rEff < 15 ? brzycki1RM(x.weight, x.reps, x.rir) : 0;
    return s + (br > 0 ? (ep + br) / 2 : ep);
  }, 0) / v.length;
}
export function calcTotalReps(sets) { return sets.reduce((s,x)=>s+(parseInt(x.reps||0)||0),0); }
// ─── RPE AUTO-DETECT — FATIGUE SLOPE MODEL ───────────────────────────────────
// Normalizza il calo di reps per il rep range di S1 (l'accuratezza RIR varia
// significativamente tra range bassi e alti — euristica applicativa).
// Output identico {rpe, label, confidence} — usato solo per display (calcAvgRPE).
// Il pipeline delle regole usa repSlope/_parseContext, non questa funzione.
export function inferRPE(sets) {
  const valid = filterWarmupSets(sets).filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  if (!valid.length) return null;
  const hasRPE = valid.some(s=>parseFloat(s.rpe)>0);
  if (hasRPE) return null;

  const reps = valid.map(s=>parseInt(s.reps));
  const first = reps[0], last = reps[reps.length-1];
  const repsLost = first - last;
  const avg = reps.reduce((a,b)=>a+b,0)/reps.length;
  const repRangeFactor = first >= 12 ? 2.0 : first >= 6 ? 1.0 : 0.5;
  const adjustedDrop = repsLost / repRangeFactor;

  if (adjustedDrop <= 0.5 && avg >= 10) return { rpe: 8.0, label: "~RPE 8",   confidence: "stimato" };
  if (adjustedDrop <= 0.5)              return { rpe: 8.5, label: "~RPE 8.5", confidence: "stimato" };
  if (adjustedDrop <= 1.0)              return { rpe: 8.5, label: "~RPE 8-9", confidence: "stimato" };
  if (adjustedDrop <= 2.0)              return { rpe: 9.0, label: "~RPE 9",   confidence: "stimato" };
  if (adjustedDrop <= 3.0)             return { rpe: 9.5, label: "~RPE 9.5", confidence: "stimato" };
  return                                       { rpe: 10,  label: "~RPE 10",  confidence: "stimato" };
}

function _stepForEquipment(type, equipment) {
  if (type === "compound") return 5;
  if (equipment && equipment.includes("manubri")) return 2;
  return 2.5;
}

function _parseContext(sets, type, metodologia, prevSets, equipment) {
  const workingSets=filterWarmupSets(sets).filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  const maxW=workingSets.length?Math.max(...workingSets.map(s=>parseFloat(s.weight))):0;
  const peakSets=workingSets.filter(s=>parseFloat(s.weight)===maxW);
  const backoffs=workingSets.filter(s=>parseFloat(s.weight)<maxW);
  const workReps=peakSets.map(s=>parseInt(s.reps));
  const n=workReps.length;
  const s1BaselineReps=workReps[0]||0;
  const repSlope=n>=2?(workReps[0]-workReps[n-1])/Math.max(1,n-1):0;
  const s1s2CrashRatio=n>=2&&workReps[0]>0?(workReps[0]-workReps[1])/workReps[0]:0;
  const step=_stepForEquipment(type, equipment);
  const maxPhysioSlope=type==="compound"?2.0:2.5;
  const hasExplicitRPE=peakSets.some(s=>parseFloat(s.rpe)>0);
  const allUniform=workingSets.length>0&&workingSets.every(s=>parseFloat(s.weight)===maxW);
  const stimReps=workingSets.map(s=>{
    const r=parseInt(s.reps)||0,rir=parseFloat(s.rir||0),rpe=parseFloat(s.rpe||0);
    const effRIR=rir>0?rir:rpe>0?Math.max(0,10-rpe):0;
    return Math.min(5,Math.max(0,r-effRIR));
  });
  const totalStimReps=stimReps.reduce((a,b)=>a+b,0);
  const prevWorkingSets=prevSets?prevSets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0):[];
  const prevMaxW=prevWorkingSets.length?Math.max(...prevWorkingSets.map(s=>parseFloat(s.weight))):0;
  return{workingSets,peakSets,backoffs,workReps,repSlope,s1s2CrashRatio,s1BaselineReps,maxW,prevMaxW,step,maxPhysioSlope,stimReps,totalStimReps,hasExplicitRPE,allUniform,metodologia,type};
}

export function calcAvgRPE(sets) {
  const working = filterWarmupSets(sets);
  const withRPE = working.filter(s=>parseFloat(s.rpe)>0);
  if (withRPE.length) return withRPE.reduce((s,x)=>s+parseFloat(x.rpe),0)/withRPE.length;
  const inferred = inferRPE(working);
  return inferred ? inferred.rpe : null;
}

export function hasInferredRPE(sets) {
  return sets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0).every(s=>!parseFloat(s.rpe));
}

export function rpeZone(r) {
  r = parseFloat(r||0); if (!r) return null;
  if (r<6)    return {text:"Junk Volume",    color:C.amber,  bg:C.amberL,  border:C.amberB,  emoji:"⚠️"};
  if (r>=10)  return {text:"Cedimento",       color:C.red,    bg:C.redL,    border:C.redB,    emoji:"🔴"};
  if (r>=9)   return {text:"Quasi cedimento", color:C.purple, bg:C.purpleL, border:C.purpleB, emoji:"🟣"};
  if (r>=7.5) return {text:"Ottimale",        color:C.green,  bg:C.greenL,  border:C.greenB,  emoji:"✅"};
  return       {text:"Moderato",              color:C.cyan,   bg:C.cyanL,   border:C.cyanB,   emoji:"🔵"};
}

// ─── RPE ACCURACY CHECK ──────────────────────────────────────────────────────
// Rileva autovalutazione RPE probabilmente erronea (reps costanti con RPE massimale).
// Basato su: Zourdos et al. (2016, JSCR, PubMed 25993662) — correlazione reps-RPE,
// errore std ±1 punto. Helms et al. (2018, PMC5712461) — sovrastima sistematica RPE.
export function progressionComment(sets, type, prevSets, repRange, equipment) {
  const valid = sets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  if (!valid.length) return null;

  const rr = repRange || REP_RANGE_DEFAULT;
  // const repsArr  = valid.map(s=>parseInt(s.reps));
  const weights  = valid.map(s=>parseFloat(s.weight));
  const step     = _stepForEquipment(type, equipment);
  const maxW     = Math.max(...weights);
  const allUniform = weights.every(w=>w===maxW);

  // RPE: usa dichiarato se presente, altrimenti inferisci dal pattern reps
  const workSets = valid.filter(s=>parseFloat(s.weight)===maxW);
  const workReps = workSets.map(s=>parseInt(s.reps));
  const hasRPEData = workSets.some(s=>parseFloat(s.rpe)>0);
  const _inferredRPE = inferRPE(sets);
  const workRPE  = workSets.map(s=>{
    if (parseFloat(s.rpe)>0) return parseFloat(s.rpe);
    return _inferredRPE ? _inferredRPE.rpe : 0;
  });

  const avgWorkRPE  = workRPE.filter(r=>r>0).length
    ? workRPE.filter(r=>r>0).reduce((a,b)=>a+b,0)/workRPE.filter(r=>r>0).length
    : 0;
  const avgWorkReps = workReps.reduce((a,b)=>a+b,0)/workReps.length;
  const wFirst = workReps[0], wLast = workReps[workReps.length-1];
  const hasExplicitRPE = workSets.some(s=>parseFloat(s.rpe)>0);
  const wFirstRPE = hasExplicitRPE ? (workRPE[0] || 0) : 0;
  const inferredNote = hasRPEData ? "" : " (RPE stimato dal pattern reps)";

  // Se nessun RPE esplicito, salta P1-P4 (basati su RPE) e vai direttamente a P5+
  if (!hasExplicitRPE) {
    // Delta vs sessione precedente
    let dt="";
    if (prevSets) {
      const pv=calcVolume(prevSets), cv=calcVolume(sets);
      const pr=calcAvg1RM(prevSets), cr=calcAvg1RM(sets);
      const pct = pv>0 ? (cv-pv)/pv*100 : 0;
      const rd  = cr-pr;
      if (pct>3)  dt=` Vol +${pct.toFixed(1)}% vs prec.`;
      if (pct<-3) dt=` Vol -${Math.abs(pct).toFixed(1)}% vs prec.`;
      if (rd>0.5) dt+=` 1RM +${rd.toFixed(1)}kg.`;
    }
    const rrType = rr[type] || rr.compound;
    const allConstant = workReps.every(r=>r===wFirst);
    if (allConstant && wFirst >= rrType.max)
      return {icon:"🚀",color:C.green,bg:C.greenL,border:C.greenB,
        title:`Pronto per +${step}kg`,
        copy:`${workSets.length}x${wFirst} reps a ${maxW}kg: sei al limite superiore del range (${rrType.max} reps). Prossima sessione porta a ${maxW+step}kg.`,
        body:`${workSets.length}x${wFirst} reps a ${maxW}kg — range target ${rrType.min}-${rrType.max} reps raggiunto. Double Progression (Zourdos 2016): porta a ${maxW+step}kg.${dt}`};
    if (wFirst >= rrType.max && !allConstant && wLast >= rrType.min)
      return {icon:"^",color:C.green,bg:C.greenL,border:C.greenB,
        title:`Tetto del range — +${step}kg`,
        copy:`S1 a ${wFirst} reps (limite range): le serie successive hanno tenuto sopra ${rrType.min} reps. Porta a ${maxW+step}kg nella prossima sessione.`,
        body:`S1: ${wFirst} reps a ${maxW}kg — limite superiore range ${rrType.max}. Serie successive ${workReps.slice(1).join("-")} reps. Aumenta a ${maxW+step}kg.${dt}`};
    if (allConstant && wFirst > rrType.max)
      return {icon:"!",color:C.amber,bg:C.amberL,border:C.amberB,
        title:"Peso leggero — intensita bassa",
        copy:`Stai facendo ${wFirst} reps costanti, sopra il range target (${rrType.min}-${rrType.max}). Il peso e troppo leggero. Aggiungi ${step}kg.`,
        body:`${workReps.join("-")} reps a ${maxW}kg — sopra il range ${rrType.min}-${rrType.max}. Stimolo sub-ottimale: reps troppo alte rispetto al range target. Porta a ${maxW+step}kg.${dt}`};
    if (!allUniform && wFirst > wLast) {
      const minW = Math.min(...weights);
      const prevWorkValid = prevSets ? prevSets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0) : [];
      const prevMaxW2 = prevWorkValid.length ? Math.max(...prevWorkValid.map(s=>parseFloat(s.weight))) : 0;
      if (prevMaxW2 > 0 && maxW > prevMaxW2)
        return {icon:"^",color:C.blue,bg:C.blueL,border:C.blueB,
          title:"Top-set attempt",
          copy:`Hai testato ${maxW}kg in S1 (${wFirst} reps), poi gestito la fatica a ${minW}kg. Continua: quando S1 raggiunge ${rrType.min} reps a ${maxW}kg, porta tutto il volume a quel peso.`,
          body:`Sessione precedente: ${prevMaxW2}kg. Oggi S1 a ${maxW}kg — progressione con gestione fatica. Consolida ${maxW}kg prima di portarlo su tutte le serie.${dt}`};
    }
    return {icon:"->",color:C.blue,bg:C.blueL,border:C.blueB,
      title:"Continua cosi",
      copy:`Punta a fare ${rrType.max} reps su ogni serie a ${maxW}kg, poi aggiungi ${step}kg.`,
      body:`Serie: ${workReps.join("-")} reps a ${maxW}kg. Range: ${rrType.min}-${rrType.max} reps. Pareggia tutte le serie a ${rrType.max} prima di +${step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}${dt}`};
  }

  // Delta vs sessione precedente
  let dt="";
  if (prevSets) {
    const pv=calcVolume(prevSets), cv=calcVolume(sets);
    const pr=calcAvg1RM(prevSets), cr=calcAvg1RM(sets);
    const pct = pv>0 ? (cv-pv)/pv*100 : 0;
    const rd  = cr-pr;
    if (pct>3)  dt=` Vol +${pct.toFixed(1)}% vs prec.`;
    if (pct<-3) dt=` ⚠ Vol −${Math.abs(pct).toFixed(1)}% vs prec.`;
    if (rd>0.5) dt+=` 1RM +${rd.toFixed(1)}kg.`;
  }

  // ── P1: Carico eccessivo — troppo poche reps con RPE massimale ───────────
  if (avgWorkReps < 5 && wFirstRPE >= 9) {
    const target = Math.floor(maxW * 0.85 / 2.5) * 2.5;
    return {icon:"📉",color:C.red,bg:C.redL,border:C.redB,
      title:"Peso troppo alto",
      copy:`Stai riuscendo a fare troppo poche reps. Per far crescere i muscoli servirebbe almeno 6 reps. Prova con ${target}kg.`,
      body:`Media ${avgWorkReps.toFixed(1)} reps @RPE${wFirstRPE}${inferredNote}: sotto le 5 reps sei in zona forza pura.\n\nProssima sessione: ${target}kg × 6-8 reps.${dt}\n\n${_evidenceDetail(75, ['schoenfeld2017load'])}`};
  }

  if (workSets.length > 1) {
    const wSecond = workReps[1] ?? wFirst;
    const dropS1S2 = wFirst - wSecond;

    // ── P2: Batteria scarica (S1→S2 crash ≥50% + RPE massimale) ─────────
    // Crollo S1→S2 >50% con RPE max = fatica sistemica, non tattica
    // (Israetel 2019: segnale di accumulo fatica sistemica)
    const s1s2CrashPct = wFirst>0 ? (wFirst-wSecond)/wFirst*100 : 0;
    if (wFirstRPE >= 9.5 && s1s2CrashPct >= 50)
      return {icon:"🪫",color:C.red,bg:C.redL,border:C.redB,
        title:"Batteria scarica",
        copy:`Hai perso meta delle reps tra la prima e la seconda serie. Riposa piu a lungo tra le serie la prossima volta.`,
        body:`Crollo prima-seconda serie: ${wFirst}→${wSecond} reps (−${s1s2CrashPct.toFixed(0)}%) con sforzo percepito ${wFirstRPE}: recupero inter-serie insufficiente.\n\nAllunga il riposo a 3+ min. Se si ripete, considera una settimana con meno serie.\n\n${_evidenceDetail(65, ['zourdos2016rir', 'schoenfeld2016rest'])}${dt}`};

    // ── P3: Ego check 3+1 (S1→S2 crash 30-49%) ──────────────────────────
    // RPE alto dalla prima + drop significativo = gestione fatica inefficiente
    if (wFirstRPE >= 9 && s1s2CrashPct >= 30) {
      const s1 = Math.floor(wFirst * 0.80);
      const s2 = Math.max(1, s1-1);
      const s3 = Math.max(1, s1-1);
      const optTotal = s1 + s2 + s3;
      const actTotal = workReps.reduce((a,b)=>a+b,0);
      const rows = [
        `  S1  ${maxW}kg  |  ${workReps[0]}r @RPE${workRPE[0]||"?"}  |  ${s1}r @RPE8 (RIR2)  ←`,
        ...workSets.slice(1,-1).map((_,i)=>`  S${i+2}  ${maxW}kg  |  ${workReps[i+1]}r @RPE${workRPE[i+1]||"?"}  |  ${i===0?s2:s3}r @RPE${i===0?"9 (RIR1)":"9.5"}  ←`),
        `  S${workSets.length}  ${maxW}kg  |  ${wLast}r @RPE${workRPE[workRPE.length-1]||"?"}  |  Max reps (target ≥${wFirst})  🎯`,
      ].join("\n");
      return {icon:"🧠",color:C.purple,bg:C.purpleL,border:C.purpleB,
        title:"Risparmia energia per le ultime serie",
        copy:`Sei partito troppo forte e le reps sono crollate. La prossima volta parti con un po' di margine in piu nella prima serie.`,
        body:`Crollo prima-seconda serie: ${s1s2CrashPct.toFixed(0)}% (${workReps.join("→")} reps) @sforzo ${wFirstRPE}${inferredNote}: volume totale ${actTotal} vs ~${optTotal} ottimali.\n\n        Hai fatto   →   Prossima sessione\n${rows}\n\nPartire con margine (2 reps di riserva in prima serie) preserva il volume complessivo.\n\n${_evidenceDetail(60, ['zourdos2016rir'])}${dt}`};
    }

    // ── P4: Recupero inter-serie insufficiente ────────────────────────────
    // Crollo brusco S1→S2 ma recupero nelle serie successive = problema di densità
    // Richiede RPE esplicito su S2 — il valore inferito non è abbastanza preciso
    if (workSets.length >= 3 && dropS1S2 >= 3 && hasExplicitRPE && workRPE[1] >= 9
        && workReps.slice(2).some(r=>r >= wSecond))
      return {icon:"⏱",color:C.amber,bg:C.amberL,border:C.amberB,
        title:"Riposa piu a lungo tra prima e seconda serie",
        copy:`Dopo la prima serie hai perso molte reps, poi hai recuperato. Il peso e giusto: basta piu riposo iniziale.`,
        body:`Crollo prima-seconda serie (${wFirst}→${wSecond} reps, -${dropS1S2}) con recupero nelle serie successive: il carico e corretto, il problema e la densita.\n\nAllunga il riposo tra prima e seconda serie di 60-90 secondi. Non ridurre il carico.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'schoenfeld2016rest'])}${dt}`};
  }

  // ── P5: Reps crescenti a parità di carico — peso sottodimensionato ──────
  // Solo con carico uniforme: crescita reps = peso troppo leggero
  if (allUniform && wLast > wFirst) {
    //const rpeFirst = workRPE[0] || 0;
    const rpeLast  = workRPE[workRPE.length-1] || 0;
    if (rpeLast >= 9)
      return {icon:"🔥",color:C.amber,bg:C.amberL,border:C.amberB,
        title:"Il peso e troppo leggero",
        copy:`Stai facendo piu reps ad ogni serie. Sei in forma. Prossima volta aggiungi ${step}kg.`,
        body:`Reps crescenti (${workReps.join("→")}) a ${maxW}kg: stai migliorando nella sessione, non cedendo. Con sforzo alto sull'ultima serie, il peso e leggero.\n\nProssima sessione: porta a ${maxW+step}kg.\n\n${_evidenceDetail(60, ['plotkin2022progression'])}${dt}`};
  }

  // ── P6: Carico non uniforme ma non per progressione — scale-down ────────
  if (!allUniform) {
    const minW = Math.min(...weights);
    const isProgressive = weights[0] < weights[weights.length-1]; // carico crescente = warm-up
    if (!isProgressive) {
      // Controlla se è un top-set attempt: maxW attuale > maxW sessione precedente
      const prevWorkValid = prevSets ? prevSets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0) : [];
      const prevMaxW = prevWorkValid.length ? Math.max(...prevWorkValid.map(s=>parseFloat(s.weight))) : 0;
      const isTopSetAttempt = prevMaxW > 0 && maxW > prevMaxW;
      if (isTopSetAttempt) {
        const rrType = rr[type] || rr.compound;
        return {icon:"🏋️",color:C.blue,bg:C.blueL,border:C.blueB,
          title:"Top-set attempt",
          copy:`Hai testato ${maxW}kg in S1 (${wFirst} reps), poi gestito la fatica a ${minW}kg. Continua: quando S1 raggiunge ${rrType.min} reps a ${maxW}kg, porta tutto il volume a quel peso.`,
          body:`Sessione precedente: ${prevMaxW}kg. Oggi S1 a ${maxW}kg — progressione deliberata con gestione fatica. Consolida ${maxW}kg in S1 prima di portarlo su tutte le serie (Double Progression, Zourdos 2016).${dt}`};
      }
      return {icon:"~",color:C.amber,bg:C.amberL,border:C.amberB,
        title:"Carico scalato durante la sessione",
        copy:`Hai ridotto da ${maxW}kg a ${minW}kg. Prossima sessione punta a mantenere ${maxW}kg su tutte le serie prima di aumentare.`,
        body:`Hai ridotto da ${maxW}kg a ${minW}kg: consolida prima di progredire. Prossima sessione punta a mantenere ${maxW}kg su tutte le serie.${dt}`};
    }
  }

  // ── P7: Volume consolidato — pronto per +carico ──────────────────────────
  // Reps costanti su tutte le work set, RPE gestito (Zourdos 2016 Double Progression)
  if (workReps.every(r=>r===wFirst) && avgWorkRPE > 0 && avgWorkRPE <= 8.5 && wFirst >= 8)
    return {icon:"🚀",color:C.green,bg:C.greenL,border:C.greenB,
      title:`Pronto per +${step}kg`,
      copy:`Hai fatto ${workSets.length}x${wFirst} reps con ancora margine. La prossima volta usa ${maxW+step}kg.`,
      body:`${workSets.length}x${wFirst} reps a ${maxW}kg, sforzo medio ${avgWorkRPE.toFixed(1)}${inferredNote}. Reps costanti con margine residuo — Double Progression: porta a ${maxW+step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}${dt}`};

  // ── P8: Reps costanti ma RPE alto — tetto del range raggiunto ────────────
  // Tutte le reps uguali ma RPE alto → il carico è al limite, aumenta
  if (workReps.every(r=>r===wFirst) && avgWorkRPE >= 9 && wFirst >= 10)
    return {icon:"↑",color:C.green,bg:C.greenL,border:C.greenB,
      title:`Tetto raggiunto: +${step}kg`,
      copy:`Hai fatto ${wFirst} reps su tutte le serie lavorando al limite. Alla prossima sessione usa ${maxW+step}kg.`,
      body:`${wFirst} reps costanti a ${maxW}kg, sforzo ${avgWorkRPE.toFixed(1)}/10${inferredNote}. Reps costanti con intensità massimale: sei al tetto del range. Porta a ${maxW+step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}${dt}`};

  // ── P9: Junk Volume ──────────────────────────────────────────────────────
  if (avgWorkRPE > 0 && avgWorkRPE < 6.5)
    return {icon:"⚠️",color:C.amber,bg:C.amberL,border:C.amberB,
      title:"Peso troppo leggero",
      copy:`Il peso scelto è troppo leggero per stimolare i muscoli. Aggiungi ${step}kg o cerca di lavorare con piu impegno.`,
      body:`Sforzo medio ${avgWorkRPE.toFixed(1)}/10${inferredNote}: con troppo margine residuo (RIR >3.5) l'intensita e probabilmente insufficiente per uno stimolo ipertrofico ottimale. Aumenta di ${step}kg o punta a sforzo 7.5+.\n\n${_evidenceDetail(80, ['refalo2023failure', 'schoenfeld2017load'])}${dt}`};

  // ── Fallback: consolida ───────────────────────────────────────────────────
  return {icon:"→",color:C.blue,bg:C.blueL,border:C.blueB,
    title:"Consolida prima di aumentare",
    copy:`Prova a fare ${Math.max(...workReps)} reps su tutte le serie prima di passare al peso successivo. Mantieni ${maxW}kg.`,
    detail:`Serie: ${workReps.join("–")} reps a ${maxW}kg${inferredNote}. Pareggia tutte le serie al valore piu alto (${Math.max(...workReps)} reps) prima di +${step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}${dt}`,
    body:`Serie: ${workReps.join("–")} reps a ${maxW}kg${inferredNote}. Pareggia tutte le serie al valore piu alto (${Math.max(...workReps)} reps) prima di aumentare il carico.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}${dt}`};
}

export function checkRpeAccuracy(sets) {
  const valid = sets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  if (valid.length<3) return null;
  const maxW = Math.max(...valid.map(s=>parseFloat(s.weight)));
  const workSets = valid.filter(s=>parseFloat(s.weight)===maxW);
  const workReps = workSets.map(s=>parseInt(s.reps));
  const workRPE  = workSets.map(s=>parseFloat(s.rpe||0));
  if (workSets.length<3) return null;
  if (!workReps.every(r=>r===workReps[0])) return null;
  if (Math.max(...workRPE)<9.5 || workReps[0]<6) return null;
  return {icon:"⚖️",color:C.cyan,bg:C.cyanL,border:C.cyanB,title:"Calibra l'RPE — dati ancora pochi",
    body:`${workReps[0]} reps costanti su ${workSets.length} serie a RPE${Math.max(...workRPE)}: reps stabili con RPE massimale suggerisce sovrastima della fatica. Prova +1 rep o +${maxW===Math.max(...valid.map(s=>parseFloat(s.weight)))?2.5:5}kg alla prossima S1.`};
}

export function fatigueRatio(exName, sessions) {
  const hist=[...sessions].sort((a,b)=>a.date.localeCompare(b.date))
    .filter(s=>s.exercises.some(e=>e.name.toLowerCase().trim()===exName.toLowerCase().trim()));
  if (hist.length<4) return null;
  const getEx=s=>s.exercises.find(e=>e.name.toLowerCase().trim()===exName.toLowerCase().trim());
  const vols=hist.map(s=>calcVolume(getEx(s).sets));
  const curr=vols[vols.length-1], avg=vols.slice(-4,-1).reduce((a,b)=>a+b,0)/3;
  if (!avg || !curr) return null;
  const drop=100-(curr/avg*100);
  if (drop<=10) return null;
  const curr1RM=calcAvg1RM(getEx(hist[hist.length-1]).sets);
  const hist1RMs=hist.slice(-4,-1).map(s=>calcAvg1RM(getEx(s).sets)).filter(v=>v>0);
  if (curr1RM>0 && hist1RMs.length>0) {
    const avgHist1RM=hist1RMs.reduce((a,b)=>a+b,0)/hist1RMs.length;
    if (curr1RM>avgHist1RM) return null;
  }
  if (drop>20) return {drop,avg:Math.round(avg),curr:Math.round(curr),status:"critical"};
  if (drop>10) return {drop,avg:Math.round(avg),curr:Math.round(curr),status:"warning"};
  return null;
}

export function plateauPredictor(exName, sessions) {
  const hist=[...sessions].sort((a,b)=>a.date.localeCompare(b.date))
    .filter(s=>s.exercises.some(e=>e.name.toLowerCase().trim()===exName.toLowerCase().trim()));
  if (hist.length<4) return null;
  const rm1s=hist.slice(-8).map(s=>calcAvg1RM(s.exercises.find(e=>e.name.toLowerCase().trim()===exName.toLowerCase().trim()).sets)).filter(v=>v>0);
  if (rm1s.length<4) return null;
  const growths=[];
  for (let i=1;i<rm1s.length;i++) if (rm1s[i-1]>0) growths.push((rm1s[i]-rm1s[i-1])/rm1s[i-1]*100);
  const avg=growths.reduce((a,b)=>a+b,0)/growths.length;
  if (avg<0)   return {avg,n:rm1s.length,status:"regressing"};
  if (avg<0.5) return {avg,n:rm1s.length,status:"plateau"};
  if (avg<1.5) return {avg,n:rm1s.length,status:"slowing"};
  return null;
}

// ─── RULE REGISTRY ────────────────────────────────────────────────────────────
const RULE_REGISTRY = [
  {
    id:"TR-01", priority:10, conflicts:["TR-03","TR-05"],
    evaluate(ctx) {
      // Richiede: RPE esplicito su S1, S2 realmente compilata (>0), crash sostenuto (S3 non recupera)
      if (ctx.workReps.length<2) return null;
      if (ctx.s1s2CrashRatio<0.5) return null;
      if (!ctx.hasExplicitRPE) return null;
      if (ctx.workReps[1]<=0) return null;
      // Se S3+ recupera a livelli di S1, non è fatica sistemica ma un calo isolato
      const s1=ctx.workReps[0],s2=ctx.workReps[1];
      if (ctx.workReps.length>=3 && ctx.workReps.slice(2).some(r=>r>=s2+Math.round((s1-s2)*0.6))) return null;
      const pct=Math.round(ctx.s1s2CrashRatio*100);
      return {ruleId:"TR-01",priority:10,status:"critical",
        primaryInsight:{icon:"🪫",color:C.red,bg:C.redL,border:C.redB,
          title:"Crollo tra le serie — recupero insufficiente",
          copy:`Hai perso molte reps tra la prima e la seconda serie. Riposa almeno 2 minuti in piu prima della prossima.`,
          detail:`Crollo S1→S2: ${s1}→${s2} reps (−${pct}%) con sforzo dichiarato. Recupero inter-serie insufficiente o fatica sistemica.\n\nSe il pattern si ripete, considera una settimana di scarico.\n\n${_evidenceDetail(65, ['schoenfeld2016rest'])}`,
          body:`Crollo S1→S2: ${s1}→${s2} reps (−${pct}%) con sforzo dichiarato.\n\nAllunga il recupero inter-serie. Se si ripete, valuta una settimana di volume ridotto.\n\n${_evidenceDetail(65, ['schoenfeld2016rest'])}`},
        nextSessionPrescription:{suggestedWeightChange:0,s1BaselineReps:ctx.s1BaselineReps,
          prescriptionNote:`Mantieni ${ctx.maxW}kg — aumenta recupero inter-serie a 3+ min`}};
    }
  },
  {
    id:"TR-02", priority:8, conflicts:[],
    evaluate(ctx) {
      if (ctx.s1BaselineReps>=4) return null;
      const target=Math.floor(ctx.maxW*0.85/ctx.step)*ctx.step;
      const collapseNote=ctx.workReps.length>=2&&ctx.workReps[1]<ctx.workReps[0]-1?` (S2: ${ctx.workReps[1]} reps)`:"";
      const delta=target-ctx.maxW;
      return {ruleId:"TR-02",priority:8,status:"critical",
        primaryInsight:{icon:"📉",color:C.red,bg:C.redL,border:C.redB,
          title:"Carico eccessivo — fuori dal range ipertrofico",
          copy:`Il peso e troppo alto. Riduci a ${target}kg e punta a 6-8 reps per allenare i muscoli in modo efficace.`,
          detail:`S1: ${ctx.s1BaselineReps} reps a ${ctx.maxW}kg${collapseNote}. Sotto le 4 reps sei in zona forza, non ipertrofia. 6+ reps = soglia minima per stimolo ipertrofico.\n\nProssima sessione: ${target}kg x 6-8 reps.\n\n${_evidenceDetail(80, ['schoenfeld2017load'])}`,
          body:`S1: ${ctx.s1BaselineReps} reps a ${ctx.maxW}kg${collapseNote}. Sotto le 4 reps sei in zona forza, non ipertrofia.\n\nProssima sessione: ${target}kg x 6-8 reps.\n\n${_evidenceDetail(80, ['schoenfeld2017load'])}`},
        nextSessionPrescription:{suggestedWeightChange:delta,s1BaselineReps:ctx.s1BaselineReps,
          prescriptionNote:`Riduci a ${target}kg — recupero prioritario`}};
    }
  },
  {
    id:"TR-03", priority:7, conflicts:[],
    evaluate(ctx) {
      if (ctx.metodologia!=="topset"||!ctx.peakSets.length) return null;
      const topReps=ctx.workReps[0];
      const backW=ctx.backoffs.length?parseFloat(ctx.backoffs[0].weight):null;
      const backPct=backW!==null?(ctx.maxW-backW)/ctx.maxW*100:null;
      const isOptimal=backPct!==null&&backPct>=10&&backPct<=15&&topReps>=6;
      const wDelta=isOptimal?ctx.step:0;
      const backNote=backPct!==null
        ?` Back-off a -${backPct.toFixed(0)}% (${backW}kg): ${backPct>=10&&backPct<=15?"range corretto.":"idealmente -10/15% dalla Top Set."}`
        :" Aggiungi 1-2 serie back-off a -10/15%.";
      return {ruleId:"TR-03",priority:7,status:isOptimal?"optimal":"warning",
        primaryInsight:{icon:"🏋️",color:C.purple,bg:C.purpleL,border:C.purpleB,
          title:"Top Set + Back-off",
          copy:isOptimal?`Struttura perfetta. Alla prossima sessione porta la serie principale a ${ctx.maxW+ctx.step}kg.`:`Continua con ${ctx.maxW}kg finche le serie di supporto diventano piu gestibili.`,
          detail:`Serie principale: ${ctx.maxW}kg x ${topReps} reps.${backNote} Autoregolazione tramite top set (Mann et al. 2010, PubMed 20543744).\n\n${_evidenceDetail(35, ['israetel2021book'])}`,
          body:`Serie principale: ${ctx.maxW}kg x ${topReps} reps. ${isOptimal?`Struttura corretta: prossima a ${ctx.maxW+ctx.step}kg.`:`Consolida a ${ctx.maxW}kg prima di progredire.`}${backNote}\n\n${_evidenceDetail(35, ['israetel2021book'])}`},
        nextSessionPrescription:{suggestedWeightChange:wDelta,s1BaselineReps:ctx.s1BaselineReps,
          prescriptionNote:isOptimal
            ?`Aumenta a ${ctx.maxW+ctx.step}kg — S1 come baseline aperta, punta a ${ctx.s1BaselineReps}+ reps`
            :`Mantieni ${ctx.maxW}kg — pareggia le ${ctx.s1BaselineReps} reps di S1`}};
    }
  },
  {
    id:"TR-04", priority:6, conflicts:[],
    evaluate(ctx) {
      if (ctx.workReps.length<3||ctx.repSlope>0.5) return null;
      const rrType = (ctx.rr||REP_RANGE_DEFAULT)[ctx.type]||(ctx.rr||REP_RANGE_DEFAULT).compound;
      const wFirst = ctx.workReps[0];
      // Senza RPE: reps costanti nel range → consolida verso il max; sopra il range → aggiungi peso
      if (!ctx.hasExplicitRPE) {
        if (wFirst >= rrType.max)
          return {ruleId:"TR-04",priority:6,status:"optimal",
            primaryInsight:{icon:"^",color:C.green,bg:C.greenL,border:C.greenB,
              title:"Pronto per aumentare il carico",
              copy:`Reps costanti su tutte le serie: sei al tetto del range. Alla prossima sessione usa ${ctx.maxW+ctx.step}kg.`,
              detail:`Pattern: ${ctx.workReps.join("→")} reps a ${ctx.maxW}kg. Serie costanti al tetto del range (${rrType.max} reps): aumenta il carico.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}`,
              body:`Pattern: ${ctx.workReps.join("→")} reps a ${ctx.maxW}kg. Al tetto del range ${rrType.max} reps: +${ctx.step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}`},
            nextSessionPrescription:{suggestedWeightChange:ctx.step,s1BaselineReps:ctx.s1BaselineReps,
              prescriptionNote:`Aumenta a ${ctx.maxW+ctx.step}kg`}};
        // Nel range: non è un problema, lascia passare a TR-05 o fallback
        return null;
      }
      // Con RPE: stesse reps ma con buffer esplicito — opportunità positiva
      return {ruleId:"TR-04",priority:6,status:"optimal",
        primaryInsight:{icon:"^",color:C.green,bg:C.greenL,border:C.greenB,
          title:"Pronto per aumentare il carico",
          copy:`Reps costanti su tutte le serie con ancora margine: il peso e gestito. Alla prossima sessione usa ${ctx.maxW+ctx.step}kg.`,
          detail:`Pattern: ${ctx.workReps.join("→")} reps a ${ctx.maxW}kg (decremento medio ${ctx.repSlope.toFixed(1)}/serie).\nSerie costanti con margine residuo: aumenta il carico.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}`,
          body:`Pattern: ${ctx.workReps.join("→")} reps a ${ctx.maxW}kg.\nMargine residuo presente: porta a ${ctx.maxW+ctx.step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}`},
        nextSessionPrescription:{suggestedWeightChange:ctx.step,s1BaselineReps:ctx.s1BaselineReps,
          prescriptionNote:`Aumenta a ${ctx.maxW+ctx.step}kg — S1 come baseline aperta, punta a ${ctx.s1BaselineReps}+ reps`}};
    }
  },
  {
    id:"TR-05", priority:5, conflicts:[],
    evaluate(ctx) {
      if (ctx.workReps.length<2||ctx.repSlope<=0.5||ctx.repSlope>ctx.maxPhysioSlope) return null;
      return {ruleId:"TR-05",priority:5,status:"optimal",
        primaryInsight:{icon:"🚀",color:C.blue,bg:C.blueL,border:C.blueB,
          title:"Progressione fisiologica — consolida",
          copy:`Le reps scendono un po' tra le serie: e normale. Continua con ${ctx.maxW}kg finche non arrivi a ${ctx.s1BaselineReps} reps su ogni serie, poi aggiungi ${ctx.step}kg.`,
          detail:`${ctx.workReps.join("→")} reps a ${ctx.maxW}kg (decremento medio ${ctx.repSlope.toFixed(1)}/serie).\nDecremento fisiologico: punta a ${ctx.s1BaselineReps} reps costanti prima di +${ctx.step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}`,
          body:`${ctx.workReps.join("→")} reps a ${ctx.maxW}kg.\nDecremento fisiologico: punta a ${ctx.s1BaselineReps} reps costanti prima di +${ctx.step}kg.\n\n${_evidenceDetail(60, ['zourdos2016rir', 'plotkin2022progression'])}`},
        nextSessionPrescription:{suggestedWeightChange:0,s1BaselineReps:ctx.s1BaselineReps,
          prescriptionNote:`Mantieni ${ctx.maxW}kg — pareggia le ${ctx.s1BaselineReps} reps di S1`}};
    }
  },
];

function _resolveConflicts(triggered) {
  const sorted=[...triggered].sort((a,b)=>b.priority-a.priority);
  const suppressed=new Set();
  return sorted.filter(r=>{
    if (suppressed.has(r.ruleId)) return false;
    const def=RULE_REGISTRY.find(x=>x.id===r.ruleId);
    (def?.conflicts||[]).forEach(id=>suppressed.add(id));
    return true;
  });
}

export function buildExerciseInsights(sets, type, prevSets, exName, allSessions, metodologia, repRange, options) {
  const exEntry=exName?findExercise(exName):null;
  const resolvedType=(exEntry?.type)||type||'compound';
  const resolvedEquipment=exEntry?.equipment||null;
  const valid=sets.filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  const empty={status:"optimal",primaryInsight:null,secondaryInsights:[],nextSessionPrescription:{suggestedWeightChange:0,s1BaselineReps:0,prescriptionNote:""}};
  if (!valid.length) return empty;
  const rr=repRange||REP_RANGE_DEFAULT;
  const ctx={..._parseContext(sets,resolvedType,metodologia,prevSets,resolvedEquipment),rr};
  const triggered=RULE_REGISTRY.map(r=>r.evaluate(ctx)).filter(Boolean);
  const kept=_resolveConflicts(triggered);
  const secondary=[...kept.slice(1).map(r=>r.primaryInsight)];
  const rpeHint=options?.showRpe!==false ? checkRpeAccuracy(sets) : null;
  if (rpeHint) secondary.push(rpeHint);
  if (allSessions&&exName) {
    const fr=fatigueRatio(exName,allSessions);
    if (fr) secondary.push(fr.status==="critical"
      ?{icon:"😴",color:C.red,bg:C.redL,border:C.redB,title:"Fatica sistemica — considera un deload",copy:`Il volume di oggi è calato molto rispetto alle ultime sessioni. Considera una settimana di scarico con meno serie e pesi leggeri.`,detail:`Vol oggi (${fr.curr}) = ${(100-fr.drop).toFixed(0)}% della media recente (${fr.avg}). Calo ${fr.drop.toFixed(1)}% — segnale di affaticamento accumulato. Valuta un deload.`,body:`Vol oggi (${fr.curr}) = ${(100-fr.drop).toFixed(0)}% della media recente (${fr.avg}). Calo ${fr.drop.toFixed(1)}% — valuta una settimana di scarico.`}
      :{icon:"⚡",color:C.amber,bg:C.amberL,border:C.amberB,title:"Accumulo fatica — monitora il recupero",copy:`Stai accumulando stanchezza. Dormi bene e mangia a sufficienza nei prossimi giorni.`,detail:`Vol oggi (${fr.curr}) = ${(100-fr.drop).toFixed(0)}% della media recente (${fr.avg}). Calo ${fr.drop.toFixed(1)}% — monitora sonno e calorie.`,body:`Vol oggi (${fr.curr}) = ${(100-fr.drop).toFixed(0)}% della media recente (${fr.avg}). Calo ${fr.drop.toFixed(1)}% — monitora sonno e calorie.`});
    const pp=plateauPredictor(exName,allSessions);
    if (pp) {
      const m={
        regressing:{icon:"📉",color:C.red,bg:C.redL,border:C.redB,title:"1RM in calo — monitora il recupero",copy:`La tua forza stimata sta calando. Prova a variare il numero di reps o a fare una settimana più leggera.`,detail:`1RM in calo (${pp.avg.toFixed(2)}%/sess su ${pp.n}). Varia rep range o inserisci deload.`,body:`1RM in calo (${pp.avg.toFixed(2)}%/sess su ${pp.n}). Varia rep range o inserisci deload.`},
        plateau:{icon:"📊",color:C.purple,bg:C.purpleL,border:C.purpleB,title:"Plateau rilevato",copy:`La progressione si è fermata. Prova a cambiare il numero di reps o ad aggiungere una serie.`,detail:`Crescita media ${pp.avg.toFixed(2)}%/sess su ${pp.n} — sotto soglia 0.5%. Prova periodizzazione ondulata o +1 serie.`,body:`Crescita media ${pp.avg.toFixed(2)}%/sess su ${pp.n} — sotto soglia 0.5%. Periodizzazione ondulata o +1 serie.`},
        slowing:{icon:"🐢",color:C.cyan,bg:C.cyanL,border:C.cyanB,title:"Progressione rallentata",copy:`Stai crescendo più lentamente, ma è del tutto normale dopo i primi mesi. Continua così.`,detail:`Crescita ${pp.avg.toFixed(2)}%/sess. Normale (curva logaritmica). Se scende ancora 2 sessioni, cambia stimolo.`,body:`Crescita ${pp.avg.toFixed(2)}%/sess. Normale (curva log). Se scende ancora 2 sessioni, cambia stimolo.`},
      };
      if (m[pp.status]) secondary.push(m[pp.status]);
    }
  }
  return {
    status:kept[0]?.status||"optimal",
    primaryInsight:kept[0]?.primaryInsight||null,
    secondaryInsights:secondary,
    nextSessionPrescription:kept[0]?.nextSessionPrescription||{suggestedWeightChange:0,s1BaselineReps:ctx.s1BaselineReps,prescriptionNote:""}
  };
}


// ─── METODOLOGIA COMMENT ─────────────────────────────────────────────────────
export function metodologiaComment(sets, type, metodologia, equipment) {
  const valid = filterWarmupSets(sets).filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  if (!valid.length || !metodologia || metodologia==="auto") return null;

  const workSets = (() => {
    const maxW = Math.max(...valid.map(s=>parseFloat(s.weight)));
    return valid.filter(s=>parseFloat(s.weight)===maxW);
  })();
  const workReps = workSets.map(s=>parseInt(s.reps));
  const workRPE  = workSets.map(s=>parseFloat(s.rpe||0));
  const maxW     = Math.max(...valid.map(s=>parseFloat(s.weight)));
  const step     = _stepForEquipment(type, equipment);
  const first=workReps[0], last=workReps[workReps.length-1];
  const dropPct  = first>0?(first-last)/first*100:0;
  const avgRPE   = workRPE.reduce((a,b)=>a+b,0)/workRPE.length;

  if (metodologia==="rpe_asc") {
    // Reps costanti = ottimo. Drop drastico = fatica già in S1.
    if (workReps.every(r=>r===first))
      return {icon:"✅",color:C.green,bg:C.greenL,border:C.greenB,
        title:"RPE Ascendente — gestione ottimale",
        body:`Reps costanti (${workReps.join("→")}) con RPE crescente: volume di qualità perfettamente gestito. ${avgRPE>=9.5?`Ultima serie a RPE${workRPE[workReps.length-1]} → prossima sessione +${step}kg.`:`Margine ancora presente → +1 rep per serie alla prossima.`}`};
    if (dropPct>25)
      return {icon:"⚠️",color:C.amber,bg:C.amberL,border:C.amberB,
        title:"RPE Ascendente — fatica precoce",
        body:`Drop del ${dropPct.toFixed(0)}% (${workReps.join("→")}) con buffer progressivo: indica fatica residua già in S1. Parti con RPE7 alla prossima e osserva se le reps si stabilizzano.`};
    return {icon:"→",color:C.blue,bg:C.blueL,border:C.blueB,
      title:"RPE Ascendente — consolida",
      body:`Drop ${dropPct.toFixed(0)}% (${workReps.join("→")}): fisiologico ma migliorabile. Porta l'ultima serie a ${first} reps prima di aumentare il carico.`};
  }

  if (metodologia==="failure") {
    // Calo reps previsto. ≤30% ottimo, 30-40% ok, >40% problema recupero.
    if (dropPct<=30)
      return {icon:"🔥",color:C.green,bg:C.greenL,border:C.greenB,
        title:"Straight to Failure — ottimo",
        body:`Drop ${dropPct.toFixed(0)}% (${workReps.join("→")}): calo fisiologico nella norma per il cedimento totale. Prossima sessione: +${step}kg se ultima serie ≥6 reps (era ${last}).`};
    if (dropPct<=40)
      return {icon:"⏱",color:C.amber,bg:C.amberL,border:C.amberB,
        title:"Straight to Failure — recupero al limite",
        body:`Drop ${dropPct.toFixed(0)}% (${workReps.join("→")}): accettabile ma al limite. Il peso è corretto — allarga il riposo tra serie di 30-60 secondi. Non ridurre il carico.`};
    return {icon:"🪫",color:C.red,bg:C.redL,border:C.redB,
      title:"Straight to Failure — recupero insufficiente",
      body:`Drop ${dropPct.toFixed(0)}% (${workReps.join("→")}): >40% indica recupero sistemico inadeguato, non peso eccessivo. Aggiungi 90 secondi di recupero inter-serie. Carico invariato.`};
  }

  if (metodologia==="topset") {
    const topReps=workReps[0], topRPE=workRPE[0];
    const backoffs=valid.filter(s=>parseFloat(s.weight)<maxW);
    const backW=backoffs.length?parseFloat(backoffs[0].weight):null;
    const backPct=backW?((maxW-backW)/maxW*100):null;
    return {icon:"🏋️",color:C.purple,bg:C.purpleL,border:C.purpleB,
      title:"Top Set + Back-off",
      body:`Top Set: ${maxW}kg × ${topReps} reps @RPE${topRPE}. ${topRPE>=9.5&&topReps>=6?`Forza reale solida → prossima Top Set a ${maxW+step}kg.`:`Consolida a ${maxW}kg prima di progredire.`}${backPct!==null?` Back-off a -${backPct.toFixed(0)}% (${backW}kg): ${backPct>=10&&backPct<=15?"range corretto.":"idealmente -10/15% dalla Top Set."}`:" Aggiungi 1-2 serie back-off a -10/15%."}`};
  }

  if (metodologia==="rpt") {
    const sorted=[...valid].sort((a,b)=>parseFloat(b.weight)-parseFloat(a.weight));
    const isDescending=valid.every((s,i)=>i===0||parseFloat(valid[i-1].weight)>=parseFloat(s.weight));
    const isRepsIncreasing=valid.every((s,i)=>i===0||parseInt(valid[i-1].reps)<=parseInt(s.reps));
    if (isDescending&&isRepsIncreasing)
      return {icon:"🔻",color:C.green,bg:C.greenL,border:C.greenB,
        title:"Reverse Pyramid — struttura corretta",
        body:`Piramide inversa rispettata: peso decrescente, reps crescenti. S1 (${sorted[0].weight}kg): se ≥ reps target → prossima sessione +${step}kg su tutte le serie scalando di conseguenza.`};
    return {icon:"⚠️",color:C.amber,bg:C.amberL,border:C.amberB,
      title:"Reverse Pyramid — struttura da correggere",
      body:`La piramide inversa richiede peso decrescente e reps crescenti ad ogni serie. ${!isDescending?"Verifica che il carico scenda ogni serie.":"Assicurati che le reps aumentino ogni serie."}`};
  }
  return null;
}

// ─── EXERCISE PREVIEW ─────────────────────────────────────────────────────────

// ─── SCHEDE MANAGER ───────────────────────────────────────────────────────────
// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export function weeklyVol(sessions) {
  const map={};
  sessions.forEach(sess=>{
    const d=new Date(sess.date),mon=new Date(d);
    mon.setDate(d.getDate()-((d.getDay()+6)%7));
    const k=mon.toISOString().split("T")[0];
    map[k]=(map[k]||0)+sess.exercises.reduce((s,e)=>s+calcVolume(e.sets),0);
  });
  return Object.entries(map).sort((a,b)=>a[0].localeCompare(b[0])).slice(-8)
    .map(([k,v])=>({week:new Date(k).toLocaleDateString("it-IT",{day:"numeric",month:"short"}),volume:Math.round(v)}));
}
export function detectStalls(sessions) {
  const MIN = 5;
  const sorted = [...sessions].sort((a,b)=>a.date.localeCompare(b.date));
  const byVol={}, byEx={};
  sorted.forEach(sess=>sess.exercises.forEach(ex=>{
    if(!byVol[ex.name])byVol[ex.name]=[];
    if(!byEx[ex.name])byEx[ex.name]=[];
    byVol[ex.name].push(calcVolume(ex.sets));
    byEx[ex.name].push(ex);
  }));
  return Object.entries(byVol).filter(([name,v])=>{
    if(v.length<MIN)return false;
    const last5vol=v.slice(-MIN);
    const volMin=Math.min(...last5vol), volMax=Math.max(...last5vol);
    const volSpread=(volMax-volMin)/(last5vol[0]||1);
    if(volSpread>=0.05)return false;
    const last5ex=byEx[name].slice(-MIN);
    const rm1s=last5ex.map(ex=>calcAvg1RM(ex.sets)).filter(r=>r>0);
    if(rm1s.length<MIN)return false;
    const rm1Min=Math.min(...rm1s), rm1Max=Math.max(...rm1s);
    const rm1Spread=(rm1Max-rm1Min)/(rm1s[0]||1);
    return rm1Spread<0.03;
  }).map(([n])=>n);
}
export function detectLoadReductions(sessions) {
  const sorted=[...sessions].sort((a,b)=>b.date.localeCompare(a.date));
  const by={};
  sorted.forEach(sess=>sess.exercises.forEach(ex=>{
    if(!by[ex.name])by[ex.name]=[];
    const maxW=Math.max(...(ex.sets||[]).map(s=>parseFloat(s.weight)||0));
    if(maxW>0)by[ex.name].push(maxW);
  }));
  return Object.entries(by)
    .filter(([,v])=>v.length>=2&&v[0]<v[1]*0.97)
    .map(([n,v])=>({name:n,delta:Math.round((v[0]-v[1])*10)/10}));
}

export function trendLabel(data,key){
  const vals=data.map(d=>d[key]).filter(v=>v!==null&&v!==undefined);
  if(vals.length<2)return null;
  const first=vals[0],last=vals[vals.length-1];
  const pct=((last-first)/first)*100;
  if(Math.abs(pct)<0.1)return{text:"→ Stabile nel periodo",color:C.muted};
  return{text:`${pct>0?"↑":"↓"} ${Math.abs(pct).toFixed(1)}% nel periodo`,color:pct>0?C.green:C.red};
}

// ─── PERFORMANCE ENGINE 4-STATE ───────────────────────────────────────────────
export function calcS1Tonnellaggio(sets) {
  const working=filterWarmupSets(sets);
  const s1=working.find(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0);
  if(!s1)return 0;
  return parseFloat(s1.weight)*parseInt(s1.reps);
}

// Returns { exerciseName: { state, color, pctChange } } for all exercises with ≥2 appearances in last 28 days.
export function classifyAllExercises4State(sessions) {
  const VOLT='#CCFF00';
  const cutoff=new Date();cutoff.setDate(cutoff.getDate()-28);
  const byEx={};
  [...sessions]
    .filter(s=>new Date(s.date)>=cutoff)
    .sort((a,b)=>a.date.localeCompare(b.date))
    .forEach(sess=>sess.exercises.forEach(ex=>{
      const n=ex.name;
      if(!byEx[n])byEx[n]={s1Vals:[],s1Weights:[]};
      const s1T=calcS1Tonnellaggio(ex.sets);
      if(s1T<=0)return;
      const working=filterWarmupSets(ex.sets);
      const s1W=parseFloat(working.find(s=>parseFloat(s.weight)>0)?.weight||0);
      byEx[n].s1Vals.push(s1T);
      byEx[n].s1Weights.push(s1W);
    }));
  const result={};
  Object.entries(byEx).forEach(([name,{s1Vals,s1Weights}])=>{
    if(s1Vals.length<2)return;
    const latest2=s1Vals.slice(-2);
    const prev3=s1Vals.slice(-5,-2);
    const curAvg=latest2.reduce((a,b)=>a+b,0)/latest2.length;
    const prevAvg=prev3.length?prev3.reduce((a,b)=>a+b,0)/prev3.length:s1Vals[0];
    const pctChange=prevAvg>0?(curAvg-prevAvg)/prevAvg*100:0;
    const maxHistWeight=s1Weights.length>1?Math.max(...s1Weights.slice(0,-1)):0;
    const lastWeight=s1Weights[s1Weights.length-1];
    let state,color;
    if(lastWeight>maxHistWeight&&s1Weights.length>1){state='PR';color=VOLT;}
    else if(pctChange>=3){state='Progressione';color=C.green;}
    else if(pctChange<=-3){state='Regressione';color=C.red;}
    else{state='Stallo';color=C.amber;}
    result[name]={state,color,pctChange};
  });
  return result;
}

// ─── SPIDER CHART METRICS ─────────────────────────────────────────────────────
// Returns [{metric,value}] normalised 0–100 for FR, SS, DL, CCS, EV.
export function calcSpiderMetrics(session,allSessions,bw){
  if(!session?.exercises?.length)return null;

  // FR — Forza Relativa: best Brzycki 1RM / BW, 2.5×BW = 100
  const bodyW=parseFloat(bw)||0;
  let bestBrzycki=0;
  session.exercises.forEach(ex=>{
    filterWarmupSets(ex.sets).forEach(s=>{
      const w=parseFloat(s.weight||0),r=parseInt(s.reps||0);
      if(w>0&&r>0&&r<37){const rm=w/(1.0278-0.0278*r);if(rm>bestBrzycki)bestBrzycki=rm;}
    });
  });
  const FR=bodyW>0?Math.min(100,Math.round(bestBrzycki/bodyW/2.5*100)):50;

  // SS — Slope Stability: CV of S1 tonnellaggio over last 4 sessions for main exercise
  const mainEx=session.exercises[0];
  const exHist=[...allSessions]
    .sort((a,b)=>a.date.localeCompare(b.date))
    .filter(s=>s.date<=session.date)
    .slice(-4)
    .map(s=>calcS1Tonnellaggio(s.exercises.find(e=>e.name===mainEx.name)?.sets||[]));
  const validHist=exHist.filter(v=>v>0);
  let SS=50;
  if(validHist.length>=2){
    const mean=validHist.reduce((a,b)=>a+b,0)/validHist.length;
    const std=Math.sqrt(validHist.reduce((s,v)=>s+(v-mean)**2,0)/validHist.length);
    const cv=mean>0?std/mean:0;
    SS=Math.max(0,Math.min(100,Math.round((1-cv/0.15)*100)));
  }

  // DL — Densità Lavorativa: session vol / personal best session vol
  const sessVol=session.exercises.reduce((s,e)=>s+calcVolume(e.sets),0);
  const allVols=allSessions.map(s=>s.exercises.reduce((t,e)=>t+calcVolume(e.sets),0));
  const maxVol=allVols.length?Math.max(...allVols):sessVol;
  const DL=maxVol>0?Math.min(100,Math.round(sessVol/maxVol*100)):50;

  // CCS — Capacità di Carico Sostenuto: secondHalf / firstHalf vol
  const exList=session.exercises;
  const half=Math.ceil(exList.length/2);
  const firstHalf=exList.slice(0,half).reduce((s,e)=>s+calcVolume(e.sets),0);
  const secondHalf=exList.slice(half).reduce((s,e)=>s+calcVolume(e.sets),0);
  const CCS=firstHalf>0?Math.min(100,Math.round(secondHalf/firstHalf*100)):50;

  // EV — Efficienza Volume: stimReps/set, 5 stimReps/set = 100
  let totalStimReps=0,totalSets=0;
  session.exercises.forEach(ex=>{
    filterWarmupSets(ex.sets).forEach(s=>{
      const r=parseInt(s.reps||0),rir=parseFloat(s.rir||0),rpe=parseFloat(s.rpe||0);
      if(parseFloat(s.weight||0)>0&&r>0){
        const effRIR=rir>0?rir:rpe>0?Math.max(0,10-rpe):2;
        totalStimReps+=Math.min(5,Math.max(0,r-effRIR));
        totalSets++;
      }
    });
  });
  const EV=totalSets>0?Math.min(100,Math.round(totalStimReps/totalSets/5*100)):50;

  return[
    {metric:'FR',value:FR},
    {metric:'SS',value:SS},
    {metric:'DL',value:DL},
    {metric:'CCS',value:CCS},
    {metric:'EV',value:EV},
  ];
}

// ─── PERFORMANCE WIDGET METRICS ───────────────────────────────────────────────

// Sets with RPE ≥ 7 OR (RIR explicitly set AND RIR ≤ 3)
export function calcHardSets(sets) {
  return filterWarmupSets(sets).filter(s => {
    const w = parseFloat(s.weight||0), r = parseInt(s.reps||0);
    if (!w || !r) return false;
    const rpe = parseFloat(s.rpe||0);
    const rir = s.rir !== '' && s.rir != null ? parseFloat(s.rir) : null;
    return rpe >= 7 || (rir !== null && rir <= 3);
  }).length;
}

export function calcSessionHardSets(exercises) {
  return exercises.reduce((sum, ex) => sum + calcHardSets(ex.sets || []), 0);
}

export function calcSessionAvgRPE(exercises) {
  const vals = exercises.flatMap(ex =>
    (ex.sets || []).filter(s => parseFloat(s.rpe) > 0).map(s => parseFloat(s.rpe))
  );
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

// Returns { current, avg4w, pct, dir } for a given metric function across sessions.
// current  = metricFn(lastSession)
// avg4w    = mean of metricFn over sessions in the last 28 days excluding the most recent
// pct      = percentage change current vs avg4w
// dir      = 'up' | 'down' | 'flat'
// ─── LOAD PROJECTION — OLS linear regression ─────────────────────────────────
// Projects avg working weight N sessions ahead using least-squares trend on historical data.
// Input: array of { date, peso } (avg working weight per session, sorted ascending)
// Output: { historical: [{date,peso}], projected: [{date,peso,isProjection:true}], slope, r2 }
export function calcLoadProjection(occurrences, stepsAhead = 4) {
  const pts = occurrences
    .map(o => {
      const ws = filterWarmupSets(o.ex.sets).map(s => parseFloat(s.weight||0)).filter(w => w > 0);
      return ws.length ? { date: o.date, peso: ws.reduce((a,b)=>a+b,0)/ws.length } : null;
    })
    .filter(Boolean);

  if (pts.length < 3) return null;

  const n = pts.length;
  const xs = pts.map((_,i) => i);
  const ys = pts.map(p => p.peso);
  const xMean = xs.reduce((a,b)=>a+b,0)/n;
  const yMean = ys.reduce((a,b)=>a+b,0)/n;
  const ssXX = xs.reduce((s,x)=>s+(x-xMean)**2, 0);
  const ssXY = xs.reduce((s,x,i)=>s+(x-xMean)*(ys[i]-yMean), 0);
  if (ssXX === 0) return null;
  const slope = ssXY / ssXX;
  const intercept = yMean - slope * xMean;

  const ssTot = ys.reduce((s,y)=>s+(y-yMean)**2, 0);
  const ssRes = ys.reduce((s,y,i)=>s+(y-(slope*xs[i]+intercept))**2, 0);
  const r2 = ssTot > 0 ? 1 - ssRes/ssTot : 0;

  // Build projected dates (approx avg gap between sessions)
  const dates = pts.map(p => new Date(p.date));
  const avgGapMs = n > 1 ? (dates[n-1]-dates[0])/(n-1) : 7*24*3600*1000;
  const lastDate = dates[n-1];

  // Decay slope toward 0 over long horizons (half-life 12 sessions) so extrapolation
  // stays physically plausible — a trend never continues indefinitely at full force.
  const floorPeso = Math.min(...ys) * 0.9;
  const lastHistPeso = ys[n - 1];
  const projected = Array.from({length: stepsAhead}, (_,i) => {
    const d = new Date(lastDate.getTime() + avgGapMs*(i+1));
    const iso = d.toISOString().slice(0,10).slice(5);
    // Sum decayed increments from last historical point
    const cumDelta = Array.from({length: i+1}, (_, k) => slope * Math.pow(0.5, k / 12))
      .reduce((a, b) => a + b, 0);
    const peso = Math.round((lastHistPeso + cumDelta) * 10) / 10;
    return { date: iso, peso: Math.max(floorPeso, peso), isProjection: true };
  });

  const historical = pts.map((p,i) => ({
    date: p.date.slice(5),
    peso: Math.round(p.peso*10)/10,
    trend: Math.round((slope*i+intercept)*10)/10,
  }));

  return { historical, projected, slope: Math.round(slope*100)/100, r2: Math.round(r2*100)/100 };
}

// ─── OVERALL PROGRESSION TREND ───────────────────────────────────────────────
// Per ogni settimana, calcola la media delle slope OLS (carico medio per sessione)
// di tutti gli esercizi con ≥5 sessioni totali.
// Ritorna array di { week: 'YYYY-MM-DD' (lunedì), avgSlope: number }
export function calcOverallProgressionTrend(sessions) {
  if (!sessions || sessions.length < 5) return [];

  // Raggruppa occorrenze per esercizio
  const exMap = {};
  sessions.forEach(sess => {
    sess.exercises.forEach(ex => {
      const key = ex.name.toLowerCase().trim();
      if (!exMap[key]) exMap[key] = [];
      const ws = (ex.sets || []).map(s => parseFloat(s.weight || 0)).filter(w => w > 0);
      if (ws.length) {
        const avg = ws.reduce((a, b) => a + b, 0) / ws.length;
        exMap[key].push({ date: sess.date, avg });
      }
    });
  });

  // Tieni solo esercizi con ≥5 occorrenze con peso
  const eligible = Object.values(exMap).filter(pts => pts.length >= 5);
  if (!eligible.length) return [];

  // Calcola slope OLS per ogni esercizio
  function olsSlope(pts) {
    const sorted = [...pts].sort((a, b) => a.date.localeCompare(b.date));
    const xs = sorted.map((_, i) => i);
    const ys = sorted.map(p => p.avg);
    return _olsSlope(xs, ys);
  }

  // Raccoglie tutte le settimane (chiave = lunedì della settimana)
  function weekKey(iso) {
    const d = new Date(iso);
    const dow = (d.getDay() + 6) % 7;
    const mon = new Date(d);
    mon.setDate(d.getDate() - dow);
    return mon.toISOString().slice(0, 10);
  }

  const allWeeks = [...new Set(sessions.map(s => weekKey(s.date)))].sort();
  if (allWeeks.length < 3) return [];

  return allWeeks.map(wk => {
    // Per ogni esercizio eligible, calcola slope usando solo sessioni fino a fine settimana
    const wkEnd = new Date(wk); wkEnd.setDate(wkEnd.getDate() + 6);
    const wkEndISO = wkEnd.toISOString().slice(0, 10);
    const slopes = eligible
      .map(pts => {
        const before = pts.filter(p => p.date <= wkEndISO);
        if (before.length < 5) return null;
        return olsSlope(before);
      })
      .filter(s => s !== null);
    if (!slopes.length) return null;
    const avg = slopes.reduce((a, b) => a + b, 0) / slopes.length;
    return { week: wk, avgSlope: Math.round(avg * 100) / 100 };
  }).filter(Boolean);
}

// ─── DELOAD PREVENTIVO ────────────────────────────────────────────────────────
// Ritorna { needed: bool, weeksSinceDeload: number }
// needed=true se ci sono 5+ settimane attive consecutive senza una settimana di scarico
// (settimana di scarico = volume settimanale < 60% della media delle settimane precedenti)
export function checkDeloadNeeded(sessions) {
  if (!sessions || sessions.length < 3) return { needed: false, weeksSinceDeload: 0 };
  const sorted = [...sessions].sort((a,b)=>a.date.localeCompare(b.date));
  const getISOWeek = d => {
    const date = new Date(d);
    date.setHours(0,0,0,0);
    date.setDate(date.getDate() + 3 - (date.getDay()+6)%7);
    const week1 = new Date(date.getFullYear(),0,4);
    return `${date.getFullYear()}-W${String(1+Math.round(((date-week1)/86400000-3+(week1.getDay()+6)%7)/7)).padStart(2,'0')}`;
  };
  const weekVols = {};
  sorted.forEach(sess => {
    const wk = getISOWeek(sess.date);
    const vol = sess.exercises.reduce((s,e)=>s+calcVolume(e.sets),0);
    weekVols[wk] = (weekVols[wk]||0) + vol;
  });
  const weeks = Object.keys(weekVols).sort();
  if (weeks.length < 3) return { needed: false, weeksSinceDeload: 0 };
  let weeksSinceDeload = 0;
  for (let i = weeks.length - 1; i >= 1; i--) {
    const curr = weekVols[weeks[i]];
    const prevVols = weeks.slice(Math.max(0,i-4),i).map(w=>weekVols[w]);
    const prevAvg = prevVols.reduce((a,b)=>a+b,0)/(prevVols.length||1);
    if (prevAvg > 0 && curr < prevAvg * 0.6) break;
    weeksSinceDeload++;
  }
  return { needed: weeksSinceDeload >= 5, weeksSinceDeload };
}

// ─── OVERREACHING CRONICO ─────────────────────────────────────────────────────
// Rileva RPE crescente a carico invariato (±5%) su 4+ sessioni consecutive
// Ritorna { detected: bool, rpeSlope: number, message: string } | null
export function detectOverreaching(sessions, exerciseName) {
  if (!sessions || !exerciseName) return null;
  const sorted = [...sessions].sort((a,b)=>a.date.localeCompare(b.date));
  const occ = sorted
    .map(s => s.exercises?.find(e => e.name.toLowerCase() === exerciseName.toLowerCase()))
    .filter(Boolean)
    .filter(ex => ex.sets?.some(s => parseFloat(s.rpe) > 0));
  if (occ.length < 4) return null;
  const last4 = occ.slice(-4);
  const sessData = last4.map(ex => {
    const working = filterWarmupSets(ex.sets).filter(s=>parseFloat(s.weight)>0&&parseInt(s.reps)>0&&parseFloat(s.rpe)>0);
    if (!working.length) return null;
    const avgW = working.reduce((s,x)=>s+parseFloat(x.weight),0)/working.length;
    const avgRPE = working.reduce((s,x)=>s+parseFloat(x.rpe),0)/working.length;
    return { avgW, avgRPE };
  }).filter(Boolean);
  if (sessData.length < 4) return null;
  const maxW = Math.max(...sessData.map(d=>d.avgW));
  const minW = Math.min(...sessData.map(d=>d.avgW));
  if (minW <= 0 || (maxW-minW)/minW > 0.05) return null;
  const xs = sessData.map((_,i)=>i);
  const ys = sessData.map(d=>d.avgRPE);
  const slope = _olsSlope(xs, ys);
  if (slope < 0.5) return null;
  return {
    detected: true,
    rpeSlope: Math.round(slope*100)/100,
    message: `RPE in aumento su ${exerciseName} a carico invariato. Valuta un giorno di recupero extra.`,
  };
}

export function metricTrend(sessions, metricFn) {
  const sorted = [...sessions].sort((a, b) => b.date.localeCompare(a.date));
  if (!sorted.length) return { current: null, avg4w: null, pct: null, dir: 'flat' };
  const current = metricFn(sorted[0]);
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 28);
  const window = sorted.slice(1).filter(s => new Date(s.date) >= cutoff);
  if (!window.length) return { current, avg4w: null, pct: null, dir: 'flat' };
  const vals = window.map(metricFn).filter(v => v !== null && v !== undefined && !isNaN(v));
  if (!vals.length) return { current, avg4w: null, pct: null, dir: 'flat' };
  const avg4w = vals.reduce((a, b) => a + b, 0) / vals.length;
  if (!avg4w) return { current, avg4w, pct: null, dir: 'flat' };
  const pct = ((current - avg4w) / avg4w) * 100;
  const dir = Math.abs(pct) < 1 ? 'flat' : pct > 0 ? 'up' : 'down';
  return { current, avg4w, pct, dir };
}

// ─── MUSCLE VOLUME ANALYSIS ───────────────────────────────────────────────────
// Mappa esercizi → muscoli con pesi 1.0 (primary) / 0.5 (secondary).
// Conta solo hard sets: RIR ≤4, oppure RPE ≥6, oppure nessun dato (benefit of doubt).
// Evidenza: Refalo 2023 (T2, score 80) — hard sets come unità di stimolo.
// Output: { volumes: { [muscle]: effectiveSets }, unmapped: [exName] }
export function calcMuscleVolume(exercises, findFn) {
  const volumes = {}, unmapped = [];
  (exercises || []).forEach(ex => {
    const db = findFn(ex.name);
    if (!db) { unmapped.push(ex.name); return; }
    const workSets = filterWarmupSets(ex.sets || []).filter(s => {
      if (!parseFloat(s.weight) || !parseInt(s.reps)) return false;
      const rir = s.rir !== '' && s.rir != null ? parseFloat(s.rir) : null;
      const rpe = parseFloat(s.rpe || 0);
      if (rir !== null) return rir <= 4;
      if (rpe > 0) return rpe >= 6;
      return true;
    }).length;
    if (!workSets) return;
    const pm = db.primaryMuscle;
    if (pm) volumes[pm] = (volumes[pm] || 0) + workSets;
    (db.muscles || []).forEach(m => {
      if (m !== pm) volumes[m] = (volumes[m] || 0) + workSets * 0.5;
    });
  });
  return { volumes, unmapped };
}

// Aggrega calcMuscleVolume su tutte le sessioni della settimana e confronta con landmarks.
// Evidenza MV/MEV/MAV: Schoenfeld 2017, Bernárdez-Vázquez 2022 (score 85-90).
// Evidenza MRV per muscolo: Israetel 2021 T5 (score 25, expertEstimate:true).
// Output: [{ muscle, effectiveSets, landmark, status, evidenceScore, expertEstimate, isCurrentWeek? }]
export function assessWeeklyVolume(weekSessions, findFn, currentWeekKey) {
  const totals = {};
  weekSessions.forEach(s => {
    const { volumes } = calcMuscleVolume(s.exercises || [], findFn);
    Object.entries(volumes).forEach(([m, v]) => { totals[m] = (totals[m] || 0) + v; });
  });
  const weekKey = weekSessions.length ? (() => {
    const d = new Date(weekSessions[0].date);
    d.setHours(0,0,0,0);
    d.setDate(d.getDate() + 3 - (d.getDay()+6)%7);
    const w1 = new Date(d.getFullYear(),0,4);
    return `${d.getFullYear()}-W${String(1+Math.round(((d-w1)/86400000-3+(w1.getDay()+6)%7)/7)).padStart(2,'0')}`;
  })() : null;
  const isCurrent = currentWeekKey && weekKey === currentWeekKey;
  return Object.entries(totals).map(([muscle, sets]) => {
    const lm = VOLUME_LANDMARKS[muscle] || VOLUME_LANDMARKS.default;
    const status =
      sets < lm.mev    ? 'under_mev' :
      sets <= lm.mav_hi ? 'in_mav' :
      sets <= lm.mrv    ? 'over_mav' : 'over_mrv';
    return {
      muscle, effectiveSets: Math.round(sets * 10) / 10, landmark: lm, status,
      evidenceScore: lm.expertEstimate ? 25 : 85,
      expertEstimate: lm.expertEstimate,
      ...(isCurrent ? { isCurrentWeek: true } : {}),
    };
  });
}

// Classifica l'intensità per ogni esercizio e aggrega a livello sessione.
// Basato su Schoenfeld 2017 (T2, score 80): ≤5 reps = forza, 6-15 = ipertrofia, >15 = endurance.
// Output: { forza: %, ipertrofia: %, endurance: %, profile, evidenceScore }
export function intensityProfile(exercises) {
  let forza = 0, ipertrofia = 0, endurance = 0;
  (exercises || []).forEach(ex => {
    const valid = filterWarmupSets(ex.sets || []).filter(s => parseFloat(s.weight)>0 && parseInt(s.reps)>0);
    const avgReps = valid.length ? valid.reduce((s,x)=>s+parseInt(x.reps),0)/valid.length : 0;
    const sets = valid.length;
    if (avgReps <= 5)       forza      += sets;
    else if (avgReps <= 15) ipertrofia += sets;
    else                    endurance  += sets;
  });
  const total = forza + ipertrofia + endurance || 1;
  const dominant = forza >= ipertrofia && forza >= endurance ? 'forza'
    : ipertrofia >= endurance ? 'ipertrofia' : 'endurance';
  const profile = (Math.max(forza,ipertrofia,endurance)/total) < 0.7 ? 'misto' : dominant;
  return {
    forza: Math.round(forza/total*100),
    ipertrofia: Math.round(ipertrofia/total*100),
    endurance: Math.round(endurance/total*100),
    profile, evidenceScore: 80,
    sources: ['schoenfeld2017load', 'refalo2023failure'],
  };
}

// Rileva squilibri push/pull e quad/hinge da un oggetto { muscle: effectiveSets }.
// Principio push/pull accettato; soglie 1.5x e 2x sono euristiche (score 40).
// Riceve muscleVolumes (plain object), NON l'output di calcMuscleVolume.
const _PUSH    = ['pettorali', 'tricipiti', 'deltoidi'];
const _PULL    = ['dorsali', 'bicipiti', 'trapezio'];
const _QUAD    = ['quadricipiti'];
const _HINGE   = ['ischio-crurali', 'glutei'];
export function balanceCheck(muscleVolumes) {
  const mv = muscleVolumes || {};
  const push  = _PUSH.reduce((s,m)=>s+(mv[m]||0), 0);
  const pull  = _PULL.reduce((s,m)=>s+(mv[m]||0), 0);
  const quad  = _QUAD.reduce((s,m)=>s+(mv[m]||0), 0);
  const hinge = _HINGE.reduce((s,m)=>s+(mv[m]||0), 0);
  const issues = [];
  if (pull > 0 && push/pull > 1.5)
    issues.push({ type:'push_pull',  ratio: push/pull,  msg: `Push/Pull: ${(push/pull).toFixed(1)}x — aggiungi volume posterior chain` });
  if (hinge > 0 && quad/hinge > 2)
    issues.push({ type:'quad_hinge', ratio: quad/hinge, msg: `Quad/Hinge: ${(quad/hinge).toFixed(1)}x — aggiungi stacchi o leg curl` });
  if (push > 0 && pull/push > 1.5)
    issues.push({ type:'pull_dominant', ratio: pull/push, msg: `Pull dominante: ${(pull/push).toFixed(1)}x — normale per back days` });
  return { push, pull, quad, hinge, issues, evidenceScore: 40 };
}

